import { AnyAction } from 'redux';

import { INotification } from 'overview/interface/Notification';

import { PaginatedItems } from 'shared/interface/PaginatedItems';

import {
  NotificationsActions,
  NotificationCountsPayload,
  SetReadStatusPayload,
  SetRelatedNotificationsPayload,
} from './notificationsActions';

interface NotificationsReducerState {
  notifications: PaginatedItems<INotification> | null;
  unreadCount: number;
  unseenCount: number;
  unreadCollaborationCount: number;
  unseenCollaborationCount: number;
  syncToolFailNotification: INotification | null;
}

const initialState: NotificationsReducerState = {
  notifications: null,
  unseenCount: 0,
  unreadCount: 0,
  unreadCollaborationCount: 0,
  unseenCollaborationCount: 0,
  syncToolFailNotification: null,
};

const applyReadStatus = (
  notif: INotification,
  payload: SetReadStatusPayload
): INotification => {
  const { notificationIds, read, markAllOrderNotifications } = payload;

  const isTargeted = notificationIds.includes(notif.id);

  const isRead = isTargeted ? read : notif.isRead;

  // the whole order was marked, so this notification's group follows along
  const isGroupMarked = !!markAllOrderNotifications && isTargeted;

  if (!notif.relatedNotifications) {
    return {
      ...notif,
      isRead,
      relatedUnreadNotificationsCount: isGroupMarked
        ? 0
        : notif.relatedUnreadNotificationsCount,
    };
  }

  const relatedNotifications = notif.relatedNotifications.map((related) =>
    isGroupMarked || notificationIds.includes(related.id)
      ? { ...related, isRead: read }
      : related
  );

  return {
    ...notif,
    isRead,
    relatedNotifications,
    relatedUnreadNotificationsCount: relatedNotifications.filter(
      (related) => !related.isRead
    ).length,
  };
};

const markNotificationAsRead = (notif: INotification): INotification => ({
  ...notif,
  isRead: true,
  relatedUnreadNotificationsCount: 0,
  ...(notif.relatedNotifications && {
    relatedNotifications: notif.relatedNotifications.map((related) => ({
      ...related,
      isRead: true,
    })),
  }),
});

export const notificationsReducer = (
  state = initialState,
  action: AnyAction
) => {
  switch (action.type) {
    case NotificationsActions.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload as PaginatedItems<INotification>,
      };
    case NotificationsActions.SET_RELATED_NOTIFICATIONS: {
      const { notificationId, relatedNotifications } =
        action.payload as SetRelatedNotificationsPayload;

      return {
        ...state,
        notifications: {
          ...state.notifications,
          items: (state.notifications?.items ?? ([] as INotification[])).map(
            (notif) =>
              notif.id === notificationId
                ? { ...notif, relatedNotifications }
                : notif
          ),
        } as PaginatedItems<INotification>,
      };
    }
    case NotificationsActions.SET_READ_STATUS: {
      const payload = action.payload as SetReadStatusPayload;

      // marking a whole order hits an unknown number of notifications, so the
      // counters are refetched from the server instead of being derived here
      const unreadDelta = payload.markAllOrderNotifications
        ? 0
        : payload.notificationIds.length * (payload.read ? -1 : 1);

      return {
        ...state,
        unreadCount: payload.showCollaborationOnly
          ? state.unreadCount
          : state.unreadCount + unreadDelta,
        unreadCollaborationCount: payload.showCollaborationOnly
          ? state.unreadCollaborationCount + unreadDelta
          : state.unreadCollaborationCount,
        notifications: {
          ...state.notifications,
          items: (state.notifications?.items ?? ([] as INotification[])).map(
            (notif) => applyReadStatus(notif, payload)
          ),
        } as PaginatedItems<INotification>,
      };
    }
    case NotificationsActions.SET_MARK_ALL_AS_READ:
      return {
        ...state,
        unreadCount: action.payload ? state.unreadCount : 0,
        unseenCount: action.payload ? state.unseenCount : 0,
        unreadCollaborationCount: action.payload
          ? 0
          : state.unreadCollaborationCount,
        unseenCollaborationCount: action.payload
          ? 0
          : state.unseenCollaborationCount,
        notifications: {
          ...state.notifications,
          items: (state.notifications?.items ?? ([] as INotification[])).map(
            markNotificationAsRead
          ),
        } as PaginatedItems<INotification>,
      };
    case NotificationsActions.SET_NOTIFS_COUNTS: {
      const {
        unreadCount,
        unseenCount,
        unreadCollaborationCount,
        unseenCollaborationCount,
      } = action.payload as NotificationCountsPayload;
      return {
        ...state,
        unseenCount,
        unreadCount,
        unreadCollaborationCount,
        unseenCollaborationCount,
      };
    }
    case NotificationsActions.SET_AS_SEEN:
      return {
        ...state,
        unseenCount: action.payload.onlyCollaboration
          ? state.unseenCount
          : state.unseenCount - (action.payload.notifs as string[]).length,
        unseenCollaborationCount: action.payload.onlyCollaboration
          ? state.unseenCollaborationCount -
            (action.payload.notifs as string[]).length
          : state.unseenCollaborationCount,
      };
    case NotificationsActions.SET_UNSEEN_COUNT:
      return {
        ...state,
        unseenCount: (state.unseenCount + action.payload) as number,
      };
    case NotificationsActions.SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: (state.unreadCount + action.payload) as number,
      };

    case NotificationsActions.SYNC_TOOL_FAIL_NOTIFICATION:
      return {
        ...state,
        syncToolFailNotification: action.payload,
      };
    case NotificationsActions.CLEAN_SYNC_TOOL_FAIL_NOTIFICATION:
      return {
        ...state,
        syncToolFailNotification: null,
      };
    default:
      return state;
  }
};
